import { convertFileSrc } from '@tauri-apps/api/core';
import debugFactory from 'debug';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { type ChatSendError, chatSendError } from '../../chat/chatSendError';
import { checkPromptInjection, promptGuardMessage } from '../../chat/promptInjectionGuard';
import { trackAnalyticsEvent } from '../../components/analytics';
import ApprovalRequestCard from '../../components/chat/ApprovalRequestCard';
import ArtifactCard from '../../components/chat/ArtifactCard';
import ChatComposer from '../../components/chat/ChatComposer';
import ChatFilesChip from '../../components/chat/ChatFilesChip';
import ChatNewWindowHero from '../../components/chat/ChatNewWindowHero';
import ComposerTokenStats from '../../components/chat/ComposerTokenStats';
import { FlowApprovalRequestCard } from '../../components/chat/FlowApprovalRequestCard';
import IntegrationConnectCard from '../../components/chat/IntegrationConnectCard';
import QueuedFollowups from '../../components/chat/QueuedFollowups';
import SuperContextToggle from '../../components/chat/SuperContextToggle';
import { whenSuperContextWriteSettled } from '../../components/chat/superContextWrite';
import WorkflowProposalCard from '../../components/chat/WorkflowProposalCard';
import { ConfirmationModal } from '../../components/intelligence/ConfirmationModal';
import { SidebarContent } from '../../components/layout/shell/SidebarSlot';
import { settingsNavState } from '../../components/settings/modal/settingsOverlay';
import UpsellBanner from '../../components/upsell/UpsellBanner';
import { dismissBanner, shouldShowBanner } from '../../components/upsell/upsellDismissState';
import { selectBackgroundProcesses } from '../../features/conversations/components/BackgroundProcessesPanel';
import {
  ChatThreadView,
  type ChatThreadViewHandle,
} from '../../features/conversations/components/ChatThreadView';
import { PlanReviewCard } from '../../features/conversations/components/PlanReviewCard';
import {
  ThreadGoalEditorPanel,
  ThreadGoalFooterTrigger,
  useThreadGoal,
} from '../../features/conversations/components/ThreadGoalChip';
import { ThreadTodoStrip } from '../../features/conversations/components/ThreadTodoStrip';
import {
  evaluateComposerSend,
  getComposerBlockedSendFeedback,
  handleComposerSlashCommand,
} from '../../features/conversations/composerSendDecision';
import { useMemorySyncActive } from '../../features/conversations/hooks/useBackgroundActivity';
import { formatResetTime } from '../../features/conversations/utils/format';
import {
  GENERAL_TAB_VALUE,
  isThreadVisibleInTab,
} from '../../features/conversations/utils/threadFilter';
import {
  ChatMascotDock,
  useChatMascotOptional,
  useChatMascotSendBinding,
} from '../../features/human/chatMascot';
import MicComposer from '../../features/human/MicComposer';
import { useFlowApprovalRequests } from '../../hooks/useFlowApprovalRequests';
import { useUsageState } from '../../hooks/useUsageState';
import {
  type Attachment,
  ATTACHMENT_MAX_FILES,
  ATTACHMENT_MAX_IMAGES,
  buildMessageWithAttachments,
  imageMarkerCost,
  parseMessageImages,
  validateAndReadFile,
} from '../../lib/attachments';
import { useT } from '../../lib/i18n/I18nContext';
import { applyOpenRouterFreeModels } from '../../services/api/openrouterFreeModels';
import { threadApi } from '../../services/api/threadApi';
import { fetchThreadTokenUsage } from '../../services/api/threadUsageApi';
import {
  aiRegenerate,
  chatCancel,
  chatClearQueue,
  chatSend,
  useRustChat,
} from '../../services/chatService';
import { callCoreRpc } from '../../services/coreRpcClient';
import {
  loadAgentProfiles,
  selectActiveAgentProfileId,
  selectAgentProfile,
  selectAgentProfiles,
} from '../../store/agentProfileSlice';
import {
  beginInferenceTurn,
  clearFollowupsForThread,
  clearRuntimeForThread,
  clearThreadSendPending,
  enqueueFollowup,
  fetchAndHydrateDerivedTranscript,
  fetchAndHydrateTurnState,
  hydrateThreadUsage,
  markThreadSendPending,
  type ProcessingTranscriptItem,
  type QueuedFollowup,
  registerParallelRequest,
  setTaskBoardForThread,
  setToolTimelineForThread,
  type ToolTimelineEntry,
} from '../../store/chatRuntimeSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectSocketStatus } from '../../store/socketSelectors';
import {
  addInferenceResponse,
  addMessageLocal,
  clearCreateThreadError,
  clearThreadInferenceActive,
  createNewThread,
  deleteThread,
  loadThreadMessages,
  loadThreads,
  markThreadInferenceActive,
  setSelectedThread,
  THREAD_NOT_FOUND_MESSAGE,
  updateThreadTitle,
} from '../../store/threadSlice';
import type { ConfirmationModal as ConfirmationModalType } from '../../types/intelligence';
import type { ThreadMessage } from '../../types/thread';
import { chatThreadPath } from '../../utils/chatRoutes';
import { CHAT_ATTACHMENTS_ENABLED } from '../../utils/config';
import { PRICING_URL } from '../../utils/links';
import { openUrl } from '../../utils/openUrl';
import {
  notifyOverlaySttState,
  openhumanVoiceStatus,
  openhumanVoiceTranscribeBytes,
  openhumanVoiceTts,
} from '../../utils/tauriCommands';
import { ThreadList } from './threadList/ThreadList';

const CHAT_MODEL_HINT = 'hint:chat';
type InputMode = 'text' | 'voice';
type ReplyMode = 'text' | 'voice';
const debug = debugFactory('conversations');

interface ConversationsProps {
  /**
   * `page` (default) renders the centered max-w-2xl card layout used as
   * a top-level route at /conversations. `sidebar` drops the centering
   * and width cap so the panel can be embedded as a right rail inside
   * another page (e.g. /accounts).
   */
  variant?: 'page' | 'sidebar';
  /**
   * Composer mode. `text` (default) uses the textarea + send button.
   * `mic-cloud` swaps the entire composer for a single mic button that
   * captures audio via `MediaRecorder`, transcribes it through the cloud
   * STT proxy, then routes the transcript through the same send path.
   * Used by the mascot tab so the only interaction is voice.
   */
  composer?: 'text' | 'mic-cloud';
  /**
   * Project the thread list into the root sidebar's dynamic region even in the
   * `sidebar` variant. Page variant always projects it; this lets an embedded
   * instance (e.g. the Human page's right-rail chat) surface the user's threads
   * in the left sidebar while keeping the chat itself on the right. The list
   * and the chat share the same selection state, so clicking a thread switches
   * the embedded conversation.
   */
  projectThreadList?: boolean;
}

// Stable empty reference so the `activeThreadIds` selector returns the same
// object identity when the slice field is absent (narrow test stores),
// avoiding spurious re-renders.
const EMPTY_ACTIVE_THREADS: Record<string, true> = {};

// Stable empty reference for the queued-follow-ups map, so the selector keeps
// the same identity when the slice field is absent (narrow test stores).
const EMPTY_QUEUED_FOLLOWUPS: Record<string, QueuedFollowup[]> = {};

// Stable empty live tool-timeline / processing-transcript for the selected
// thread. A fresh `[]` here took a new identity every render, invalidating the
// `backgroundProcesses` memo below on each pass and adding avoidable re-render
// churn to the chat's hot path (#5162).
const EMPTY_TOOL_TIMELINE: ToolTimelineEntry[] = [];
const EMPTY_PROCESSING: ProcessingTranscriptItem[] = [];

export function isComposerInteractionBlocked(args: {
  /** Whether the *currently selected* thread has an in-flight inference turn. */
  selectedThreadActive: boolean;
  rustChat: boolean;
}): boolean {
  return !args.rustChat || args.selectedThreadActive;
}

interface ImeKeyboardEventLike {
  isComposing?: boolean;
  keyCode?: number;
  which?: number;
  nativeEvent?: { isComposing?: boolean; keyCode?: number; which?: number };
}

export function isImeCompositionKeyEvent(event: ImeKeyboardEventLike): boolean {
  return (
    event.isComposing === true ||
    event.nativeEvent?.isComposing === true ||
    event.nativeEvent?.keyCode === 229 ||
    event.nativeEvent?.which === 229 ||
    event.keyCode === 229 ||
    event.which === 229
  );
}

/**
 * Normalise the value thrown out of `dispatch(loadThreads()).unwrap()` into a
 * displayable string. `createAsyncThunk` re-throws Redux's `SerializedError`
 * (a plain object, not an `Error` instance) when the thunk rejects — which is
 * why the original Sentry report (OPENHUMAN-REACT-X) showed up as
 * "Non-Error promise rejection captured with value: …" rather than a stack.
 * Exported so the mount-effect's `.catch` stays a one-liner and the message
 * shape can be unit-tested without mounting the full page.
 */
export function formatThreadLoadError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return String(err);
}

/**
 * What the error strip above the composer renders: this turn's send failure if
 * there is one, otherwise a thread-create failure recorded by `threadSlice`.
 *
 * A create that blew the 30 s RPC budget used to have no surface at all — the
 * shell's "New chat" / Home actions caught the rejection and dropped it, so the
 * button just did nothing, and a call site that forgot to catch turned the same
 * failure into `UnhandledRejection: … threads_create_new timed out after
 * 30000ms` (#5156). Routing the slice-recorded failure through the existing
 * banner gives every create path one visible outcome. Exported so the precedence
 * rule is unit-testable without mounting the page.
 */
export function deriveChatErrorBanner(
  sendError: ChatSendError | null,
  createThreadError: string | null,
  createThreadFailedMessage: string
): ChatSendError | null {
  if (sendError) return sendError;
  if (createThreadError) {
    return chatSendError('create_thread_failed', createThreadFailedMessage);
  }
  return null;
}

const Conversations = ({
  variant = 'page',
  composer: composerProp = 'text',
  projectThreadList = false,
}: ConversationsProps = {}) => {
  const [composerOverride, setComposerOverride] = useState<'mic-cloud' | 'text' | null>(null);
  const composer = composerOverride ?? composerProp;
  const { t } = useT();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { threadId: routeThreadId } = useParams<{ threadId?: string }>();
  const shouldSyncChatRoute = variant === 'page' && location.pathname.startsWith('/chat');
  const { threads, selectedThreadId, messages, isLoadingMessages, messagesError } = useAppSelector(
    state => state.thread
  );
  // Optional-chain + default: narrow test stores may omit `activeThreadIds`.
  const activeThreadIds = useAppSelector(
    state => state.thread.activeThreadIds ?? EMPTY_ACTIVE_THREADS
  );
  // Per-thread inference tracking (parallel inference): the selected thread's
  // own in-flight state gates the composer; a turn running on a *different*
  // thread no longer locks this one. `firstActiveThreadId` is a best-effort
  // fallback for thread-scoped chips/panels when no thread is selected.
  const selectedThreadActive = selectedThreadId
    ? Boolean(activeThreadIds[selectedThreadId])
    : false;
  const firstActiveThreadId = Object.keys(activeThreadIds)[0] ?? null;

  // Thread-goal controller shared by the footer trigger (under the composer)
  // and the editor panel (above the composer).
  const threadGoal = useThreadGoal(selectedThreadId ?? null);

  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Imperative handle onto the transcript's own background-processes panel
  // (its state now lives inside `ChatThreadView`) so the header badge below
  // can still open it without lifting that state back up.
  const threadViewRef = useRef<ChatThreadViewHandle>(null);
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [replyMode, setReplyMode] = useState<ReplyMode>('text');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<string | null>(null);
  const [isPlayingReply, setIsPlayingReply] = useState(false);
  // Measured height of the floating composer footer (page variant only). The
  // footer is `absolute`ly positioned over the scroll area, so the message list
  // needs matching bottom padding to keep its tail visible. Defaults to 128px
  // (the old static `pb-32`) so layout is unchanged until the ResizeObserver
  // reports a real height — and grows automatically when the queued-followups
  // panel, approval cards, or error banners expand the footer (#4268).
  const [composerFooterHeight, setComposerFooterHeight] = useState(128);
  // Thread-list filtering is fixed to the General bucket — the in-sidebar
  // General/Subconscious/Tasks chips were removed. Subconscious reflections and
  // task/worker threads have dedicated surfaces (Intelligence, Tasks board).
  const selectedLabel = GENERAL_TAB_VALUE;
  const [threadSearch, setThreadSearch] = useState('');
  const [sendError, setSendError] = useState<ChatSendError | null>(null);
  // Recorded by the slice for *every* create path (#5156) — including the shell's
  // "New chat" button and the home-nav shortcut, which have no UI of their own —
  // so a failed create always has somewhere to show up.
  // Optional-chain + default, same as `activeThreadIds` below: narrow test
  // stores predate this field.
  const createThreadError = useAppSelector(state => state.thread.createThreadError ?? null);
  const [attachError, setAttachError] = useState<ChatSendError | null>(null);
  const [sendAdvisory, setSendAdvisory] = useState<string | null>(null);
  // Refs mirroring error/advisory state for effects that read them without
  // depending on them, preventing the classic "effect→setState→re-fire" cascade
  // that contributes to "Maximum update depth exceeded" (TAURI-REACT-2G).
  const sendErrorRef = useRef(sendError);
  sendErrorRef.current = sendError;
  const createThreadErrorRef = useRef(createThreadError);
  createThreadErrorRef.current = createThreadError;
  const displayedSendError = deriveChatErrorBanner(
    sendError,
    createThreadError,
    t('chat.createThreadFailed')
  );
  const sendAdvisoryRef = useRef(sendAdvisory);
  sendAdvisoryRef.current = sendAdvisory;
  const [openRouterStatus, setOpenRouterStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  // Threads whose send is mid-flight (dispatched locally, backend not yet
  // accepted). A Set so concurrent sends to different threads each track their
  // own pending state instead of clobbering a single slot.
  const [pendingSendingThreadIds, setPendingSendingThreadIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const addPendingSendingThread = useCallback(
    (threadId: string) => {
      // Mirror to Redux so global surfaces (e.g. the New Chat shortcut) can see
      // an in-flight send before any message/streaming state exists.
      dispatch(markThreadSendPending({ threadId }));
      setPendingSendingThreadIds(prev => {
        if (prev.has(threadId)) return prev;
        const next = new Set(prev);
        next.add(threadId);
        return next;
      });
    },
    [dispatch]
  );
  const removePendingSendingThread = useCallback(
    (threadId: string) => {
      dispatch(clearThreadSendPending({ threadId }));
      setPendingSendingThreadIds(prev => {
        if (!prev.has(threadId)) return prev;
        const next = new Set(prev);
        next.delete(threadId);
        return next;
      });
    },
    [dispatch]
  );
  const socketStatus = useAppSelector(selectSocketStatus);
  const agentProfiles = useAppSelector(selectAgentProfiles);
  const selectedAgentProfileId = useAppSelector(selectActiveAgentProfileId);
  // Optional chain because narrow test stores (e.g. Conversations.test
  // bootstraps without the locale slice) shouldn't crash here. `'en'`
  // matches the no-locale-directive branch in the core, so legacy
  // behaviour stays intact.
  const uiLocale = useAppSelector(state => state.locale?.current ?? 'en');
  const toolTimelineByThread = useAppSelector(state => state.chatRuntime.toolTimelineByThread);
  const interruptedAssistantByThread = useAppSelector(
    state => state.chatRuntime.interruptedAssistantByThread
  );
  const processingByThread = useAppSelector(state => state.chatRuntime.processingByThread);
  const taskBoardByThread = useAppSelector(state => state.chatRuntime.taskBoardByThread);
  const inferenceStatusByThread = useAppSelector(
    state => state.chatRuntime.inferenceStatusByThread
  );
  const artifactsByThread = useAppSelector(state => state.chatRuntime.artifactsByThread);
  const pendingApprovalByThread = useAppSelector(
    state => state.chatRuntime.pendingApprovalByThread
  );
  // Flow-approval surface (chat): a paused tinyflows run's gate, pushed via
  // the `flow_approval_request` socket event. Not thread-scoped — the
  // payload carries no `thread_id` — so it's tracked independently of the
  // selected thread and surfaced regardless of which one is open.
  const { requests: flowApprovalRequests, dismiss: dismissFlowApprovalRequest } =
    useFlowApprovalRequests();
  const pendingPlanReviewByThread = useAppSelector(
    state => state.chatRuntime.pendingPlanReviewByThread
  );
  const pendingWorkflowProposalsByThread = useAppSelector(
    state => state.chatRuntime.pendingWorkflowProposalsByThread
  );
  const streamingAssistantByThread = useAppSelector(
    state => state.chatRuntime.streamingAssistantByThread
  );
  // #4270: per-thread liveness counter bumped on each `inference_heartbeat`.
  // Watched by the silence-timer rearm effect so a long prefill / buffered
  // reasoning phase that streams no other progress still keeps the timer armed.
  const inferenceHeartbeatByThread = useAppSelector(
    state => state.chatRuntime.inferenceHeartbeatByThread
  );
  const inferenceTurnLifecycleByThread = useAppSelector(
    state => state.chatRuntime.inferenceTurnLifecycleByThread
  );
  const queuedFollowupsByThread = useAppSelector(
    state => state.chatRuntime.queuedFollowupsByThread ?? EMPTY_QUEUED_FOLLOWUPS
  );
  const rustChat = useRustChat();
  // Inline thread-title rename in the sidebar thread list — keyed by the
  // thread id being edited (null = none) so any row can rename in place.
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const editTitleInputRef = useRef<HTMLInputElement>(null);
  const ignoreNextTitleBlurRef = useRef(false);

  const {
    teamUsage,
    isAtLimit,
    isNearLimit,
    isFreeTier,
    shouldShowBudgetCompletedMessage,
    usagePct,
    // #3767: gate on the tier for the selected chat mode — Quick runs on the
    // `chat` tier, Reasoning on the `reasoning` tier — so the credits prompt
    // reflects the mode the user actually picked.
  } = useUsageState(selectedAgentProfileId === 'reasoning' ? 'reasoning' : 'chat');
  const [deleteModal, setDeleteModal] = useState<ConfirmationModalType>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [resolvedModel, setResolvedModel] = useState<string | null>(null);
  // Whether the resolved model for the active profile accepts image input.
  // Managed tiers do; custom/BYOK models only when the user flagged them. Gates
  // the composer's image-attachment affordance (docs flow regardless). Resolved
  // against the non-attachment hint so the affordance is stable as you attach.
  const [modelSupportsVision, setModelSupportsVision] = useState(false);
  // Whether a vision-capable delegate (the `vision` sub-agent) is reachable.
  // When it is, an image may be attached and routed to that sub-agent even if
  // the active orchestrator model is non-vision — the orchestrator sees a text
  // placeholder and delegates the image to the vision sub-agent. Resolved from
  // the `vision` workload tier (vision-v1 on the managed backend, or the BYOK
  // model routed to the Vision workload).
  const [visionDelegateAvailable, setVisionDelegateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = agentProfiles.find(p => p.id === selectedAgentProfileId);
        // Resolve the actually-selected profile's model so `modelSupportsVision`
        // reflects the real tier, AND the vision workload so we know whether a
        // vision sub-agent can take the image. Documents are text-extracted so
        // any model handles them.
        const hint = profile?.modelOverride ?? CHAT_MODEL_HINT;
        const [res, visionRes] = await Promise.all([
          callCoreRpc<{ model: string; vision?: boolean }>({
            method: 'openhuman.inference_resolve_model',
            params: { hint },
          }),
          callCoreRpc<{ model: string; vision?: boolean }>({
            method: 'openhuman.inference_resolve_model',
            params: { hint: 'hint:vision' },
          }).catch(() => ({ model: '', vision: false })),
        ]);
        if (!cancelled) {
          setResolvedModel(res.model);
          setModelSupportsVision(res.vision === true);
          setVisionDelegateAvailable(visionRes.vision === true);
        }
      } catch {
        if (!cancelled) {
          setResolvedModel(null);
          setModelSupportsVision(false);
          setVisionDelegateAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentProfiles, selectedAgentProfileId]);

  // Display name for share cards (#5006): the active agent profile, or the
  // product name when no named profile is selected.
  const shareAgentName =
    agentProfiles.find(p => p.id === selectedAgentProfileId)?.name ?? 'OpenHuman';

  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const composerFooterRef = useRef<HTMLDivElement>(null);
  const isComposingTextRef = useRef(false);
  // One-shot guard for the Stop/ESC partial-preservation path (#4862): request
  // ids whose partial reply has already been persisted, so a repeated Stop/ESC
  // fired before the `cancelled` event clears the live stream can't append the
  // same partial twice.
  const stoppedRequestIdsRef = useRef<Set<string>>(new Set());
  // Threads with an in-flight send, guarding against double-submit to the SAME
  // thread. Per-thread (a Set) so a send to thread B isn't blocked by an
  // in-flight send to thread A.
  const pendingSendsRef = useRef<Set<string>>(new Set());
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const replyAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastSpokenMessageIdRef = useRef<string | null>(null);
  // Per-thread silence timers. Each in-flight turn gets its own 120s safety
  // timer keyed by thread id, so concurrent turns on different threads don't
  // share (and clobber) a single timeout.
  const sendingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Ref so the mount-time dictation event handler can call the latest send fn.
  const handleSendMessageRef = useRef<((text?: string) => Promise<void>) | null>(null);
  // Per-thread "turn signature": the last-seen tuple of progress-slice
  // references [inferenceStatus, streamingAssistant, toolTimeline, taskBoard]
  // for each thread that owns a live silence timer. Redux Toolkit (immer)
  // only produces new references for the thread whose slice actually changed,
  // so comparing references lets the rearm effect (a) detect a turn completing
  // (status defined → undefined) and (b) rearm a thread's timer ONLY when that
  // thread's own state changed — unrelated threads' activity must not keep a
  // foreground turn's timer alive.
  const turnSignatureByThreadRef = useRef<Map<string, readonly unknown[]>>(new Map());

  const getAudioExtension = (mimeType: string): string => {
    const lower = mimeType.toLowerCase();
    if (lower.includes('webm')) return 'webm';
    if (lower.includes('ogg')) return 'ogg';
    if (lower.includes('wav')) return 'wav';
    if (lower.includes('mp4') || lower.includes('mpeg') || lower.includes('aac')) return 'm4a';
    return 'webm';
  };
  const canUseMicrophoneApi =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function';

  const handleCreateNewThread = async () => {
    try {
      const thread = await dispatch(createNewThread()).unwrap();
      dispatch(setSelectedThread(thread.id));
      void dispatch(loadThreadMessages(thread.id));
      if (shouldSyncChatRoute) {
        debug('[chat][route] created thread thread=%s navigate=true', thread.id);
        navigate(chatThreadPath(thread.id));
      } else {
        debug('[chat][route] created thread thread=%s navigate=false', thread.id);
      }
    } catch (error) {
      debug('[chat] create thread failed: %O', error);
      setSendError(chatSendError('create_thread_failed', t('chat.createThreadFailed')));
    }
  };

  const handleUseOpenRouterFree = async () => {
    setOpenRouterStatus('saving');
    try {
      await applyOpenRouterFreeModels();
      setOpenRouterStatus('idle');
    } catch (err) {
      console.warn('[chat] applyOpenRouterFreeModels failed', err);
      setOpenRouterStatus('error');
    }
  };

  const handleStartEditTitle = (threadId: string) => {
    const thr = threads.find(t => t.id === threadId);
    debug('[chat] thread rename: start thread=%s', threadId);
    setEditTitleValue(thr?.title ?? '');
    ignoreNextTitleBlurRef.current = true;
    setEditingThreadId(threadId);
    const scheduleSelect = window.requestAnimationFrame ?? window.setTimeout;
    scheduleSelect(() => {
      editTitleInputRef.current?.select();
      ignoreNextTitleBlurRef.current = false;
    });
  };

  const handleCommitTitle = (threadId: string) => {
    const trimmed = editTitleValue.trim();
    setEditingThreadId(null);
    // Title length only — never log the title text itself (may carry PII).
    if (!threadId || !trimmed) {
      debug('[chat] thread rename: commit skipped thread=%s empty=%s', threadId, !trimmed);
      return;
    }
    const currentTitle = threads.find(t => t.id === threadId)?.title?.trim();
    if (trimmed === currentTitle) {
      debug('[chat] thread rename: commit skipped thread=%s (unchanged)', threadId);
      return;
    }
    debug('[chat] thread rename: commit thread=%s len=%d', threadId, trimmed.length);
    void dispatch(updateThreadTitle({ threadId, title: trimmed }))
      .unwrap()
      .then(() => debug('[chat] thread rename: committed thread=%s', threadId))
      .catch(err =>
        debug(
          '[chat] thread rename: failed thread=%s err=%s',
          threadId,
          err instanceof Error ? err.message : String(err)
        )
      );
  };

  const handleSelectAgentProfile = async (profileId: string) => {
    try {
      await dispatch(selectAgentProfile(profileId)).unwrap();
    } catch (error) {
      debug('agent profile select failed: %o', error);
    }
  };

  // Seed the composer footer with the selected thread's persisted token/cost
  // usage (read back from its session transcripts) so the totals reflect prior
  // turns instead of starting at zero. Best-effort; live turns accumulate on top
  // via recordChatTurnUsage and a brand-new thread (hasUsage=false) is left as-is.
  useEffect(() => {
    if (!selectedThreadId) return;
    let cancelled = false;
    void fetchThreadTokenUsage(selectedThreadId)
      .then(u => {
        if (cancelled || !u.hasUsage) return;
        dispatch(
          hydrateThreadUsage({
            threadId: u.threadId,
            inputTokens: u.inputTokens,
            outputTokens: u.outputTokens,
            cachedTokens: u.cachedInputTokens,
            costUsd: u.costUsd,
            turns: u.turnCount,
            contextWindow: u.contextWindow,
            lastTurnInputTokens: u.lastTurnInputTokens,
            lastTurnOutputTokens: u.lastTurnOutputTokens,
            subAgents: u.subagents,
          })
        );
      })
      .catch(() => {
        /* best-effort seed; the footer still fills from live turns */
      });
    return () => {
      cancelled = true;
    };
  }, [selectedThreadId, dispatch]);

  useEffect(() => {
    let cancelled = false;

    void dispatch(loadThreads())
      .unwrap()
      .then(data => {
        if (cancelled) return;
        // Match the sidebar's default General filter here so initial/resume
        // selection can't auto-pick a thread hidden by the selected tab.
        const visibleThreads = data.threads.filter(t => isThreadVisibleInTab(t, GENERAL_TAB_VALUE));
        // An explicit "open this session" intent (e.g. View work from the Agent
        // Tasks board) wins over passive resume — and bypasses the General-tab
        // visibility filter so a task-labelled session thread can actually be
        // opened (the resume default below only considers General threads).
        const openThreadId =
          routeThreadId ?? (location.state as { openThreadId?: string } | null)?.openThreadId;
        const openThread = openThreadId ? data.threads.find(t => t.id === openThreadId) : undefined;
        if (openThread) {
          // An explicit open intent (e.g. View work from the Tasks board) opens
          // the thread in the main pane directly; the thread list itself stays
          // filtered to General.
          dispatch(setSelectedThread(openThread.id));
          void dispatch(loadThreadMessages(openThread.id));
          debug('[chat][route] opened requested thread thread=%s', openThread.id);
          return;
        }
        if (openThreadId) {
          debug('[chat][route] requested thread not found thread=%s; falling back', openThreadId);
          navigate('/chat', { replace: true });
          return;
        }
        // Restore the thread the user last had open — persisted across reloads
        // via redux-persist on the `thread` slice, and kept in-memory across
        // in-app navigation — whenever it still exists server-side. This must
        // run BEFORE the General-only default below: a non-General active
        // session (task / worker / subconscious / meeting) is filtered out of
        // `visibleThreads`, so without this branch, navigating away from the
        // Chat tab and back would drop the active thread and either resume an
        // unrelated General thread or spawn a fresh chat — losing the
        // conversation the user was in (#chat-tab-active-thread).
        const persistedThread = selectedThreadId
          ? data.threads.find(t => t.id === selectedThreadId)
          : undefined;
        if (persistedThread) {
          dispatch(setSelectedThread(persistedThread.id));
          void dispatch(loadThreadMessages(persistedThread.id));
          debug('[chat][route] restored active thread thread=%s', persistedThread.id);
          return;
        }
        // Default landing is a fresh "new window" (the merged Home surface) —
        // we no longer resume the last conversation on open. Reuse an existing
        // empty thread if one is lying around so repeated opens don't pile up
        // blank threads; otherwise create a new one. Past conversations stay
        // reachable from the thread list (clicking one selects it directly).
        const emptyThread = visibleThreads.find(t => (t.messageCount ?? 0) === 0);
        if (emptyThread) {
          dispatch(setSelectedThread(emptyThread.id));
          void dispatch(loadThreadMessages(emptyThread.id));
        } else {
          void handleCreateNewThread();
        }
      })
      .catch(err => {
        if (cancelled) return;
        debug('loadThreads failed on mount: %s', formatThreadLoadError(err));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, routeThreadId]);

  useEffect(() => {
    if (selectedThreadId) {
      void dispatch(loadThreadMessages(selectedThreadId));
      void dispatch(fetchAndHydrateTurnState(selectedThreadId));
      // Per-turn history: each past answer's own process trail. Phase C derives
      // this from the append-only transcript projection
      // (`threads_transcript_get`), auto-falling back to the legacy
      // `turn_state_history` hydration when the derived path is off, errors, or
      // the thread has no persisted transcript (legacy thread).
      void dispatch(fetchAndHydrateDerivedTranscript(selectedThreadId));
      void threadApi
        .getTaskBoard(selectedThreadId)
        .then(board => {
          if (board) {
            dispatch(setTaskBoardForThread({ threadId: selectedThreadId, board }));
          }
        })
        .catch(error => {
          debug('getTaskBoard failed: %o', error);
        });
    }
  }, [selectedThreadId, dispatch]);

  useEffect(() => {
    void dispatch(loadAgentProfiles())
      .unwrap()
      .catch(error => {
        debug('agent profiles load failed: %o', error);
      });
  }, [dispatch]);

  useEffect(() => {
    const onDictationInsert = (event: Event) => {
      const customEvent = event as CustomEvent<{ text?: string; autoSend?: boolean }>;
      const text = customEvent.detail?.text?.trim();
      if (!text) return;

      customEvent.preventDefault();

      // When autoSend is set (hotkey dictation), dispatch the transcript directly
      // to the agent without going through the text composer.
      if (customEvent.detail?.autoSend) {
        void handleSendMessageRef.current?.(text);
        return;
      }

      setInputMode('text');
      setInputValue(prev => {
        const base = prev.trim();
        if (!base) return text;
        return `${base}${base.endsWith(' ') ? '' : ' '}${text}`;
      });

      window.requestAnimationFrame(() => {
        textInputRef.current?.focus();
      });
    };

    window.addEventListener('dictation://insert-text', onDictationInsert as EventListener);
    return () =>
      window.removeEventListener('dictation://insert-text', onDictationInsert as EventListener);
  }, []);

  useEffect(() => {
    if (sendErrorRef.current && inputValue.length > 0) {
      setSendError(null);
    }
    // The store-recorded create failure (#5156) dismisses on the same signal:
    // the user is composing, so they have seen it.
    if (createThreadErrorRef.current && inputValue.length > 0) {
      dispatch(clearCreateThreadError());
    }
    if (sendAdvisoryRef.current && inputValue.length > 0) {
      setSendAdvisory(null);
    }
    // Reads sendError/sendAdvisory through refs to avoid re-firing when they
    // are cleared — which would cascade into extra render cycles and contribute
    // to "Maximum update depth exceeded" (TAURI-REACT-2G).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const clearSilenceTimer = useCallback((threadId: string) => {
    const existing = sendingTimeoutsRef.current.get(threadId);
    if (existing) {
      clearTimeout(existing);
      sendingTimeoutsRef.current.delete(threadId);
    }
  }, []);

  const armSilenceTimer = (threadId: string) => {
    clearSilenceTimer(threadId);
    const timeout = setTimeout(() => {
      debug(`armSilenceTimer: no inference signal for 120s — clearing runtime (${threadId})`);
      setSendError(chatSendError('safety_timeout', t('chat.safetyTimeout')));
      dispatch(clearRuntimeForThread({ threadId }));
      dispatch(clearThreadInferenceActive(threadId));
      sendingTimeoutsRef.current.delete(threadId);
      // Reset so the NEXT send to this thread starts from a clean baseline —
      // otherwise the rearm effect could read this turn's last signature as a
      // stale "previous" and mis-handle the next send's first signal.
      turnSignatureByThreadRef.current.delete(threadId);
      pendingSendsRef.current.delete(threadId);
      removePendingSendingThread(threadId);
    }, 120_000);
    sendingTimeoutsRef.current.set(threadId, timeout);
  };

  // Rearm the silence timer on every inference signal for the sending
  // thread. Top-level tool / iteration events bump `inferenceStatusByThread`;
  // pure-text streams (no tools) only bump `streamingAssistantByThread`;
  // sub-agent activity (a delegated `Research`/`Tools Agent`/`Memory Tree`
  // turn whose tools run in a child task) bumps `toolTimelineByThread` and
  // `taskBoardByThread` without necessarily re-emitting a top-level status
  // change, so all four must be watched — otherwise a long sub-agent loop
  // would trip the safety timer mid-run even though the user can see the
  // delegated tools firing in the timeline. When the status is cleared
  // (chat_done / chat_error), drop the timer — the completion handlers
  // own UI cleanup.
  //
  // Rearm each live silence timer when its OWN thread shows progress, and drop
  // it when that thread's turn completes. With parallel inference several
  // timers may be live at once, so we iterate every thread that currently owns
  // a timer. Per-thread reference comparison (see `turnSignatureByThreadRef`)
  // ensures an unrelated thread's activity does NOT rearm this thread's timer,
  // while still catching pure-text streams and sub-agent tool/board activity
  // that bump the other slices without re-emitting a top-level status.
  //
  // The done-transition (status defined → undefined) is detected per thread to
  // distinguish "turn just finished (chat_done / chat_error)" from "status
  // never set yet" — the Send handler dispatches `setToolTimelineForThread([])`
  // immediately after arming, firing this effect before any status publishes.
  useEffect(() => {
    for (const threadId of Array.from(sendingTimeoutsRef.current.keys())) {
      const current = [
        inferenceStatusByThread[threadId],
        streamingAssistantByThread[threadId],
        toolTimelineByThread[threadId],
        taskBoardByThread[threadId],
        // #4270: liveness beat. Kept LAST so the done-transition probe on
        // `current[0]` (status) is unaffected; a beat alone still flips the
        // `changed` check and rearms the timer through a silent reasoning phase.
        inferenceHeartbeatByThread[threadId],
      ] as const;
      const previous = turnSignatureByThreadRef.current.get(threadId);
      const status = current[0];
      const previousStatus = previous?.[0];
      if (status === undefined && previousStatus !== undefined) {
        clearSilenceTimer(threadId);
        turnSignatureByThreadRef.current.delete(threadId);
        continue;
      }
      const changed = !previous || previous.some((value, index) => value !== current[index]);
      if (!changed) continue;
      turnSignatureByThreadRef.current.set(threadId, current);
      armSilenceTimer(threadId);
    }
    // armSilenceTimer / clearSilenceTimer are stable (refs + dispatch);
    // depending on the progress maps rearms live timers on every signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inferenceStatusByThread,
    streamingAssistantByThread,
    toolTimelineByThread,
    taskBoardByThread,
    inferenceHeartbeatByThread,
  ]);

  useEffect(() => {
    return () => {
      mediaRecorderRef.current?.stop();
      mediaStreamRef.current?.getTracks().forEach(track => track.stop());
      replyAudioRef.current?.pause();
      replyAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (inputMode === 'text' && isRecording) {
      mediaRecorderRef.current?.stop();
    }
  }, [inputMode, isRecording]);

  useEffect(() => {
    if (inputMode === 'voice') {
      setReplyMode('voice');
    } else if (replyMode === 'voice') {
      setReplyMode('text');
    }
  }, [inputMode, replyMode]);

  // Proactively check voice binary availability when switching to voice mode
  useEffect(() => {
    if (inputMode !== 'voice' || !rustChat) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await openhumanVoiceStatus();
        if (cancelled) return;
        if (!status.stt_available) {
          setVoiceStatus(
            'Voice input needs a speech model to work. Go to Settings > Local AI Models to set it up.'
          );
        } else {
          setVoiceStatus('Ready — tap "Start Talking" to record.');
        }
      } catch {
        if (!cancelled) {
          setVoiceStatus('Could not check voice availability.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inputMode, rustChat]);

  const handleSlashCommand = (command: string): boolean => {
    const decision = handleComposerSlashCommand(command);
    if (decision.kind === 'not_handled') return false;

    setInputValue('');
    void handleCreateNewThread();
    return true;
  };

  const handleAttachFiles = async (files: FileList | File[] | null) => {
    if (!files) return;
    let acceptedFileCount = attachments.filter(attachment => attachment.kind === 'file').length;
    // Images and videos share one image-marker budget (video = its frames), so
    // track consumed markers rather than per-kind counts.
    let acceptedImageMarkers = attachments.reduce(
      (sum, attachment) => sum + imageMarkerCost(attachment.kind),
      0
    );
    for (const file of Array.from(files)) {
      const result = await validateAndReadFile(
        file,
        acceptedImageMarkers,
        acceptedFileCount,
        // Allow images AND video when the active model is vision-capable OR a
        // vision sub-agent can take it (orchestrator delegates the image/frames
        // onward). Video is sampled into still frames that ride the same path.
        modelSupportsVision || visionDelegateAvailable
      );
      if ('error' in result) {
        const { error } = result;
        if (error.code === 'image_not_supported') {
          setAttachError(
            chatSendError('attachment_invalid', t('chat.attachment.imageNotSupported'))
          );
        } else if (error.code === 'video_not_supported') {
          setAttachError(
            chatSendError('attachment_invalid', t('chat.attachment.videoNotSupported'))
          );
        } else if (error.code === 'too_many') {
          // image/video share the image-marker budget → tooMany; files separate.
          const key =
            error.kind === 'file' ? 'chat.attachment.tooManyFiles' : 'chat.attachment.tooMany';
          setAttachError(
            chatSendError('attachment_invalid', t(key).replace('{max}', String(error.max)))
          );
        } else if (error.code === 'too_large') {
          const maxMb = (error.maxBytes / (1024 * 1024)).toFixed(0);
          setAttachError(
            chatSendError(
              'attachment_invalid',
              t('chat.attachment.tooLarge').replace('{max}', `${maxMb} MB`)
            )
          );
        } else if (error.code === 'unsupported_type') {
          setAttachError(chatSendError('attachment_invalid', t('chat.attachment.unsupportedType')));
        } else {
          setAttachError(chatSendError('attachment_invalid', t('chat.attachment.readFailed')));
        }
        return;
      }
      if (result.attachment.kind === 'file') {
        acceptedFileCount++;
      } else {
        acceptedImageMarkers += imageMarkerCost(result.attachment.kind);
      }
      setAttachments(prev => [...prev, result.attachment]);
    }
  };

  const handleSendMessage = async (text?: string) => {
    // Guard double-submit to the SAME thread only; a send to another thread
    // may proceed concurrently.
    if (selectedThreadId && pendingSendsRef.current.has(selectedThreadId)) return;

    const normalized = text ?? inputValue;
    const trimmedInput = normalized.trim();

    if (handleSlashCommand(trimmedInput)) return;

    const sendDecision = evaluateComposerSend({
      rawText: normalized,
      selectedThreadId,
      composerInteractionBlocked,
      isAtLimit,
      socketStatus,
    });
    const trimmed = sendDecision.trimmedText;

    if (
      (sendDecision.blockReason === 'empty_input' && attachments.length === 0) ||
      sendDecision.blockReason === 'missing_thread' ||
      sendDecision.blockReason === 'composer_blocked'
    ) {
      return;
    }

    const promptGuard = checkPromptInjection(trimmed);
    if (promptGuard.verdict === 'review' || promptGuard.verdict === 'block') {
      setSendAdvisory(promptGuardMessage(promptGuard));
    } else {
      setSendAdvisory(null);
    }

    if (
      !sendDecision.shouldSend &&
      !(sendDecision.blockReason === 'empty_input' && attachments.length > 0)
    ) {
      const blockedFeedback = getComposerBlockedSendFeedback(sendDecision.blockReason);
      if (blockedFeedback) {
        setSendError(chatSendError(blockedFeedback.error.code, blockedFeedback.error.message));
      }
      return;
    }

    const sendingThreadId = selectedThreadId;
    if (!sendingThreadId) return;
    pendingSendsRef.current.add(sendingThreadId);
    addPendingSendingThread(sendingThreadId);
    // If the user just flipped the Super Context toggle, make sure that config
    // write has landed before the core builds this thread's session (which
    // reads `context.super_context_enabled`). Done AFTER the duplicate-send
    // guard above is set so this await can't open a check→add race for rapid
    // repeat clicks. Resolves instantly when nothing is pending.
    await whenSuperContextWriteSettled();
    const pendingAttachments = attachments.slice();
    const modelOverride =
      agentProfiles.find(p => p.id === selectedAgentProfileId)?.modelOverride ?? CHAT_MODEL_HINT;
    const messageText = buildMessageWithAttachments(trimmed, pendingAttachments);
    const userMessage: ThreadMessage = {
      id: `msg_${globalThis.crypto.randomUUID()}`,
      content: trimmed,
      type: 'text',
      extraMetadata:
        pendingAttachments.length > 0
          ? {
              attachmentCount: pendingAttachments.length,
              attachmentNames: pendingAttachments.map(a => a.file.name),
              attachmentKinds: pendingAttachments.map(a => a.kind),
              attachmentDataUris: pendingAttachments
                .filter(a => a.kind === 'image')
                .map(a => a.previewUri ?? a.dataUri),
              // Poster (first frame) per attachment, index-aligned with
              // attachmentKinds — only video entries carry one; others null.
              attachmentPosters: pendingAttachments.map(a =>
                a.kind === 'video' ? (a.previewUri ?? a.dataUri) : null
              ),
              attachmentCompressed: pendingAttachments.map(a => a.compressed),
            }
          : {},
      sender: 'user',
      createdAt: new Date().toISOString(),
    };

    try {
      await dispatch(addMessageLocal({ threadId: sendingThreadId, message: userMessage })).unwrap();
    } catch (error) {
      // RTK's unwrap() re-throws the rejectWithValue payload directly (a plain
      // string, not an Error). Check for the stale-thread sentinel before
      // coercing to a display string so this guard doesn't accidentally match
      // unrelated errors whose `.toString()` happens to equal the sentinel.
      if (error === THREAD_NOT_FOUND_MESSAGE) {
        setSendError(null);
        pendingSendsRef.current.delete(sendingThreadId);
        removePendingSendingThread(sendingThreadId);
        return;
      }
      const msg = error instanceof Error ? error.message : String(error);
      setSendError(chatSendError('cloud_send_failed', msg));
      pendingSendsRef.current.delete(sendingThreadId);
      removePendingSendingThread(sendingThreadId);
      return;
    }
    setInputValue('');
    setAttachments([]);
    setSendError(null);
    setAttachError(null);
    // Silence timer: fires only if 600s pass without ANY inference progress
    // (tool call, tool result, iteration start, subagent event, text delta).
    // The effect below rearms this timer whenever `inferenceStatusByThread`
    // changes for `sendingThreadId`, so long-running agent turns stay alive
    // as long as the backend is emitting signals. A truly hung server still
    // fails fast.
    // Fresh send: clear the previous-status baseline before arming so the
    // first inference signal of this turn isn't misread as a chat-done
    // transition (defined → undefined) left over from the prior turn.
    turnSignatureByThreadRef.current.delete(sendingThreadId);
    armSilenceTimer(sendingThreadId);
    dispatch(setToolTimelineForThread({ threadId: sendingThreadId, entries: [] }));
    dispatch(beginInferenceTurn({ threadId: sendingThreadId }));
    dispatch(markThreadInferenceActive(sendingThreadId));

    // ── Cloud socket path ─────────────────────────────────────────────────────
    // Always route primary chat through the cloud backend via socket.
    // Local model (Ollama) is used only for supplementary features
    // (auto-react, autocomplete, etc.) — never as a primary chat path.
    try {
      await chatSend({
        threadId: sendingThreadId,
        message: messageText,
        model: modelOverride,
        profileId: selectedAgentProfileId,
        locale: uiLocale,
      });
      trackAnalyticsEvent('chat_message_sent', {
        send_mode: 'standard',
        has_attachments: pendingAttachments.length > 0,
      });
      // Backend accepted the send; lifecycle ('started' → 'streaming') now
      // owns the `isSending` UI lock. Release the pending guard so the next
      // user turn isn't blocked by a stale ref/state.
      pendingSendsRef.current.delete(sendingThreadId);
      removePendingSendingThread(sendingThreadId);

      // Active-thread reset happens in the global ChatRuntimeProvider events.
    } catch (err) {
      // Chat loop errors are emitted via socket events; this catch handles emit-level failures.
      clearSilenceTimer(sendingThreadId);
      turnSignatureByThreadRef.current.delete(sendingThreadId);
      const msg = err instanceof Error ? err.message : String(err);
      if (
        msg.toLowerCase().includes('blocked by a security policy') ||
        msg.toLowerCase().includes('flagged for security review')
      ) {
        const code = msg.toLowerCase().includes('flagged for security review')
          ? 'prompt_review'
          : 'prompt_blocked';
        setSendError(chatSendError(code, msg));
      } else {
        setSendError(chatSendError('cloud_send_failed', msg));
      }
      dispatch(clearRuntimeForThread({ threadId: sendingThreadId }));
      dispatch(clearThreadInferenceActive(sendingThreadId));
      pendingSendsRef.current.delete(sendingThreadId);
      removePendingSendingThread(sendingThreadId);
    }
  };

  handleSendMessageRef.current = handleSendMessage;

  // Send a PARALLEL (forked) turn on the selected thread — runs concurrently
  // with the in-flight turn instead of interrupting it (queue_mode 'parallel').
  // Kept separate from `handleSendMessage` so it never touches the primary
  // turn's lifecycle (silence timer, active marker, pending guard); the forked
  // turn streams into its own lane (registered via `registerParallelRequest`)
  // and renders as an interleaved branch bubble.
  const handleSendParallel = async (text?: string) => {
    if (!rustChat || !selectedThreadId) return;
    const threadId = selectedThreadId;
    const normalized = (text ?? inputValue).trim();
    if (!normalized && attachments.length === 0) return;

    const pendingAttachments = attachments.slice();
    const modelOverride =
      agentProfiles.find(p => p.id === selectedAgentProfileId)?.modelOverride ?? CHAT_MODEL_HINT;
    const messageText = buildMessageWithAttachments(normalized, pendingAttachments);
    const userMessage: ThreadMessage = {
      id: `msg_${globalThis.crypto.randomUUID()}`,
      content: normalized,
      type: 'text',
      extraMetadata:
        pendingAttachments.length > 0
          ? {
              attachmentCount: pendingAttachments.length,
              attachmentNames: pendingAttachments.map(a => a.file.name),
              attachmentKinds: pendingAttachments.map(a => a.kind),
              attachmentDataUris: pendingAttachments
                .filter(a => a.kind === 'image')
                .map(a => a.previewUri ?? a.dataUri),
              // Poster (first frame) per attachment, index-aligned with
              // attachmentKinds — only video entries carry one; others null.
              attachmentPosters: pendingAttachments.map(a =>
                a.kind === 'video' ? (a.previewUri ?? a.dataUri) : null
              ),
              attachmentCompressed: pendingAttachments.map(a => a.compressed),
              parallelBranch: true,
            }
          : { parallelBranch: true },
      sender: 'user',
      createdAt: new Date().toISOString(),
    };

    try {
      await dispatch(addMessageLocal({ threadId, message: userMessage })).unwrap();
    } catch (error) {
      if (error === THREAD_NOT_FOUND_MESSAGE) return;
      const msg = error instanceof Error ? error.message : String(error);
      setSendError(chatSendError('cloud_send_failed', msg));
      return;
    }

    setInputValue('');
    setAttachments([]);
    setSendError(null);

    try {
      const requestId = await chatSend({
        threadId,
        message: messageText,
        model: modelOverride,
        profileId: selectedAgentProfileId,
        locale: uiLocale,
        queueMode: 'parallel',
      });
      if (requestId) {
        dispatch(registerParallelRequest({ threadId, requestId }));
      }
      trackAnalyticsEvent('chat_message_sent', {
        send_mode: 'parallel',
        has_attachments: pendingAttachments.length > 0,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSendError(chatSendError('cloud_send_failed', msg));
    }
  };

  // Queue a FOLLOW-UP on the selected thread while a turn is streaming
  // (queue_mode 'followup'): the backend sends it as a fresh turn once the
  // current turn finishes. We do NOT insert it into the transcript now —
  // appending it mid-stream would persist it BEFORE the in-flight assistant
  // reply (the conversation store is an append log), so the prompt would show
  // out of order on reload. Instead we record a queued-follow-up pill; the pill
  // is flushed into the transcript (persisted, in order, after the assistant
  // reply) when the turn ends — see `ChatRuntimeProvider`'s done/error paths.
  const handleSendFollowup = async (text?: string) => {
    if (!rustChat || !selectedThreadId) return;
    const threadId = selectedThreadId;
    const normalized = (text ?? inputValue).trim();
    const pendingAttachments = attachments.slice();
    if (!normalized && pendingAttachments.length === 0) return;

    const modelOverride =
      agentProfiles.find(p => p.id === selectedAgentProfileId)?.modelOverride ?? CHAT_MODEL_HINT;
    const messageText = buildMessageWithAttachments(normalized, pendingAttachments);
    // Build the full user message exactly like a normal send (content +
    // attachment metadata) so the follow-up persists identically when it is
    // flushed into the transcript on turn end. Guard `crypto.randomUUID` like
    // the rest of the codebase (threadSlice) for runtimes that lack it.
    const messageId = `msg_${
      globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    }`;
    const followupMessage: ThreadMessage = {
      id: messageId,
      content: normalized,
      type: 'text',
      extraMetadata:
        pendingAttachments.length > 0
          ? {
              attachmentCount: pendingAttachments.length,
              attachmentNames: pendingAttachments.map(a => a.file.name),
              attachmentKinds: pendingAttachments.map(a => a.kind),
              attachmentDataUris: pendingAttachments
                .filter(a => a.kind === 'image')
                .map(a => a.previewUri ?? a.dataUri),
              // Poster (first frame) per attachment, index-aligned with
              // attachmentKinds — only video entries carry one; others null.
              attachmentPosters: pendingAttachments.map(a =>
                a.kind === 'video' ? (a.previewUri ?? a.dataUri) : null
              ),
              attachmentCompressed: pendingAttachments.map(a => a.compressed),
            }
          : {},
      sender: 'user',
      createdAt: new Date().toISOString(),
    };
    // Never render a blank pill for an attachments-only follow-up: fall back to
    // the attachment file names as the label.
    const label = normalized || pendingAttachments.map(a => a.file.name).join(', ');

    setSendError(null);
    setAttachError(null);

    try {
      await chatSend({
        threadId,
        message: messageText,
        model: modelOverride,
        profileId: selectedAgentProfileId,
        locale: uiLocale,
        queueMode: 'followup',
      });
      // Only clear the composer once the backend has accepted the queue, so a
      // failed send leaves the user's draft + attachments intact to retry.
      setInputValue('');
      setAttachments([]);
      dispatch(enqueueFollowup({ threadId, message: followupMessage, label }));
      trackAnalyticsEvent('chat_message_sent', {
        send_mode: 'followup',
        has_attachments: pendingAttachments.length > 0,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSendError(chatSendError('cloud_send_failed', msg));
    }
  };

  // Dismiss every queued follow-up for the selected thread. Clear the backend
  // run-queue FIRST and only drop the local pills if it succeeded — on failure
  // the backend still holds (and will dispatch) the follow-ups, so keep the
  // pills and surface the error rather than falsely showing them removed.
  const handleClearQueuedFollowups = async () => {
    if (!selectedThreadId) return;
    const threadId = selectedThreadId;
    const dropped = await chatClearQueue(threadId);
    if (dropped === null) {
      setSendError(chatSendError('cloud_send_failed', t('chat.queuedFollowups.clearFailed')));
      return;
    }
    dispatch(clearFollowupsForThread({ threadId }));
  };

  // The composer's Send button (and plain Enter) route to a queued follow-up
  // while the selected thread is streaming, otherwise to a normal send.
  const handleComposerSend = (text?: string): Promise<void> =>
    selectedThreadActive ? handleSendFollowup(text) : handleSendMessage(text);

  // Cancel the in-flight turn for the selected thread. Shared by the in-composer
  // Stop button (text mode), the ESC-to-interrupt shortcut, and the footer
  // Cancel control (mic-cloud / voice modes) so the cancel path lives in one
  // place.
  //
  // Any assistant text already streamed for this turn is persisted as its own
  // message flagged `stopped: true` so the partial output stays in the
  // transcript (clearly marked) instead of vanishing when the `cancelled`
  // chat_error clears the live streaming preview (#4862). The matching
  // `onError` path deliberately appends no message for `cancelled`, so this can
  // never double-render the partial reply.
  //
  // Persistence is gated on the cancel actually being accepted: `chatCancel`
  // resolves `false` (no throw) when the socket is down or the RPC is rejected,
  // and in that case the original turn may keep running and later append its
  // own final response — so persisting a partial here would leave a
  // misleading/duplicate bubble. On failure we release the one-shot claim so a
  // retry can still preserve the partial once cancellation succeeds.
  const handleStopGeneration = useCallback(() => {
    if (!selectedThreadId) {
      debug('[chat] stop generation: no selected thread — noop');
      return;
    }
    const threadId = selectedThreadId;
    const streaming = streamingAssistantByThread[threadId];
    const partial = streaming?.content ?? '';
    const requestId = streaming?.requestId;
    // Claim the turn synchronously so a second Stop/ESC in the same tick (before
    // the cancel round-trips) can't queue a duplicate persist.
    const shouldPersist =
      partial.trim().length > 0 && (!requestId || !stoppedRequestIdsRef.current.has(requestId));
    if (shouldPersist && requestId) stoppedRequestIdsRef.current.add(requestId);
    debug(
      '[chat] stop generation: thread=%s request=%s partialLen=%d willPersist=%s',
      threadId,
      requestId ?? 'none',
      partial.trim().length,
      shouldPersist
    );
    void chatCancel(threadId).then(cancelled => {
      debug('[chat] stop generation: chatCancel thread=%s ok=%s', threadId, cancelled);
      if (!cancelled) {
        // Cancel not accepted: don't leave a misleading partial, and release the
        // claim so a later Stop/ESC can persist once cancellation goes through.
        if (shouldPersist && requestId) stoppedRequestIdsRef.current.delete(requestId);
        return;
      }
      if (shouldPersist) {
        void dispatch(
          addInferenceResponse({
            content: partial,
            threadId,
            extraMetadata: { stopped: true, ...(requestId ? { requestId } : {}) },
          })
        ).then(() => debug('[chat] stop generation: persisted stopped reply thread=%s', threadId));
      }
    });
  }, [selectedThreadId, streamingAssistantByThread, dispatch]);

  const transcribeAndSendAudio = async (mimeType: string) => {
    setIsRecording(false);
    mediaRecorderRef.current = null;
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;

    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (chunks.length === 0) {
      notifyOverlaySttState('cancelled');
      setVoiceStatus('No audio captured. Try again.');
      return;
    }

    setIsTranscribing(true);
    setVoiceStatus('Transcribing with Whisper…');
    try {
      const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
      const audioBytes = Array.from(new Uint8Array(await blob.arrayBuffer()));
      const extension = getAudioExtension(mimeType || blob.type);

      // Build conversation context from recent messages for LLM cleanup.
      const recentMessages = messages.slice(-10);
      const context =
        recentMessages.length > 0
          ? recentMessages.map(m => `${m.sender}: ${m.content}`).join('\n')
          : undefined;

      const result = await openhumanVoiceTranscribeBytes(audioBytes, extension, context);
      const transcript = result.text.trim();

      if (!transcript) {
        notifyOverlaySttState('cancelled');
        setVoiceStatus('No speech detected. Try again.');
        return;
      }

      notifyOverlaySttState('transcription_done', transcript);
      setVoiceStatus(`Heard: ${transcript}`);
      await handleSendMessage(transcript);
    } catch (err) {
      notifyOverlaySttState('error');
      const message = err instanceof Error ? err.message : String(err);
      const isSetupIssue =
        message.includes('whisper') ||
        message.includes('binary not found') ||
        message.includes('STT model');
      setSendError(
        chatSendError(
          isSetupIssue ? 'stt_not_ready' : 'voice_transcription',
          isSetupIssue
            ? 'Voice input needs a speech model. Go to Settings to download one.'
            : `Voice transcription failed: ${message}`
        )
      );
      setVoiceStatus(null);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleVoiceRecordToggle = async () => {
    if (!rustChat || selectedThreadActive || isTranscribing) return;
    if (!canUseMicrophoneApi) {
      setSendError(
        chatSendError(
          'microphone_unavailable',
          'Microphone capture is unavailable in this runtime. Use Text mode, or run the desktop app bundle with microphone permissions enabled.'
        )
      );
      return;
    }

    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',
      ];
      const supportedType = preferredTypes.find(type => MediaRecorder.isTypeSupported(type));
      const recorder = supportedType
        ? new MediaRecorder(stream, { mimeType: supportedType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        notifyOverlaySttState('error');
        setIsRecording(false);
        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
        setSendError(chatSendError('microphone_recording', 'Microphone recording failed.'));
      };
      recorder.onstop = () => {
        void transcribeAndSendAudio(recorder.mimeType);
      };

      mediaRecorderRef.current = recorder;
      setVoiceStatus('Listening… click Stop to send.');
      setSendError(null);
      setIsRecording(true);
      recorder.start();
      notifyOverlaySttState('recording_started');
    } catch (err) {
      notifyOverlaySttState('error');
      const message = err instanceof Error ? err.message : String(err);
      setSendError(chatSendError('microphone_access', `Microphone access failed: ${message}`));
      setVoiceStatus(null);
    }
  };

  useEffect(() => {
    const latestAgentMessage = [...messages].reverse().find(m => m.sender === 'agent');
    if (!latestAgentMessage) return;

    if (replyMode === 'text') {
      lastSpokenMessageIdRef.current = latestAgentMessage.id;
      replyAudioRef.current?.pause();
      replyAudioRef.current = null;
      setIsPlayingReply(false);
      return;
    }

    if (!rustChat || latestAgentMessage.id === lastSpokenMessageIdRef.current) return;

    lastSpokenMessageIdRef.current = latestAgentMessage.id;
    let cancelled = false;
    setIsPlayingReply(true);

    void (async () => {
      try {
        const ttsResult = await openhumanVoiceTts(latestAgentMessage.content);
        if (cancelled) return;

        const audioSrc = convertFileSrc(ttsResult.output_path);
        const audio = new window.Audio(audioSrc);
        replyAudioRef.current?.pause();
        replyAudioRef.current = audio;

        await audio.play();
      } catch {
        if (!cancelled) {
          setSendError(chatSendError('voice_playback', 'Failed to play voice reply.'));
        }
      } finally {
        if (!cancelled) {
          setIsPlayingReply(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, replyMode, rustChat]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isComposingTextRef.current || isImeCompositionKeyEvent(e)) return;

    // ESC while the selected thread is streaming interrupts the turn AND
    // restores the user's last prompt into the composer for re-editing in
    // place (#4862). Interrupt always fires; the prompt is only re-hydrated
    // when the composer is empty so a follow-up the user already started
    // typing is never clobbered. When nothing is streaming, ESC is left to its
    // default behaviour (blur / no-op).
    if (e.key === 'Escape' && selectedThreadActive) {
      e.preventDefault();
      const composerEmpty = inputValue.trim().length === 0;
      debug(
        '[chat] esc interrupt: thread=%s composerEmpty=%s',
        selectedThreadId ?? 'none',
        composerEmpty
      );
      handleStopGeneration();
      if (composerEmpty) {
        // Restore the last *visible* user prompt (hidden system/injected
        // messages are excluded here to match how the transcript is rendered).
        const lastUserMessage = [...messages]
          .reverse()
          .find(m => m.sender === 'user' && !m.extraMetadata?.hidden);
        const restored = lastUserMessage
          ? parseMessageImages(lastUserMessage.content ?? '').text
          : '';
        if (restored.length > 0) {
          debug('[chat] esc interrupt: restored prompt len=%d', restored.length);
          setInputValue(restored);
          window.requestAnimationFrame(() => {
            const ta = textInputRef.current;
            if (!ta) return;
            ta.focus();
            ta.setSelectionRange(restored.length, restored.length);
          });
        }
      }
      return;
    }

    // Cmd/Ctrl+Enter sends a PARALLEL branch when the selected thread already
    // has a turn in flight (otherwise it behaves like a normal send).
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (selectedThreadActive) {
        void handleSendParallel();
      } else {
        void handleSendMessage();
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // While the selected thread is streaming, a plain Enter queues a
      // follow-up (sent after the current turn) instead of being blocked.
      if (selectedThreadActive) {
        void handleSendFollowup();
      } else {
        void handleSendMessage();
      }
    }
  };

  // NOTE: the transcript-local derivations that used to live here (copy,
  // sub-agent drawer, past-turn timelines, agent insights, streaming preview,
  // etc.) moved into `ChatThreadView` (`./components/ChatThreadView.tsx`),
  // which now owns the message-list rendering keyed by `threadId` instead of
  // the global `selectedThreadId`. What remains here is what the header badge
  // and the composer footer still need directly.
  const selectedThreadToolTimeline = selectedThreadId
    ? (toolTimelineByThread[selectedThreadId] ?? EMPTY_TOOL_TIMELINE)
    : EMPTY_TOOL_TIMELINE;
  const selectedThreadProcessing = selectedThreadId
    ? (processingByThread[selectedThreadId] ?? EMPTY_PROCESSING)
    : EMPTY_PROCESSING;
  // Detached background sub-agents (mode === 'async') spawned in this thread.
  // Kept here (in addition to ChatThreadView's own copy) because the header's
  // background-processes badge needs the count/status without reaching into
  // the transcript component.
  const backgroundProcesses = useMemo(
    () => selectBackgroundProcesses(selectedThreadToolTimeline),
    [selectedThreadToolTimeline]
  );
  const runningBackgroundCount = backgroundProcesses.filter(p => p.status === 'running').length;
  // Poll-free live signal: lights the badge when memories are syncing even if
  // no sub-agent is running and the panel is closed.
  const memorySyncActive = useMemorySyncActive();
  const selectedTaskBoard = selectedThreadId ? (taskBoardByThread[selectedThreadId] ?? null) : null;
  const hasTaskBoard = Boolean(selectedTaskBoard?.cards.length);
  // A plan the orchestrator parked for interactive review (request_plan_review
  // gate). When present, the PlanReviewCard renders above the composer and
  // resolves the parked turn; the todo strip stays read-only progress.
  const pendingPlanReview = selectedThreadId
    ? (pendingPlanReviewByThread[selectedThreadId] ?? null)
    : null;
  // A candidate automation the agent drafted via `propose_workflow` (issue B4),
  // awaiting the user's Save/Dismiss decision on `WorkflowProposalCard`. Unlike
  // `pendingPlanReview`, the underlying tool call already completed — this
  // just controls whether the card is still showing.
  const pendingWorkflowProposal = selectedThreadId
    ? (pendingWorkflowProposalsByThread[selectedThreadId] ?? null)
    : null;
  const visibleMessages = messages.filter(msg => !msg.extraMetadata?.hidden);
  const hasVisibleMessages = visibleMessages.length > 0;
  const selectedStreamingAssistant = selectedThreadId
    ? (streamingAssistantByThread[selectedThreadId] ?? null)
    : null;
  // The partial reply an interrupted turn left behind (restore-fidelity fix 2):
  // surfaced as a settled, marked-interrupted bubble on restore so a turn that
  // crashed mid-answer keeps its visible work instead of rendering blank.
  const selectedInterruptedAssistant = selectedThreadId
    ? (interruptedAssistantByThread[selectedThreadId] ?? null)
    : null;
  // Blocks all composer interaction while a turn is in-flight or Rust chat is unavailable.
  // isSending: the *selected* thread is in-flight (drives selected-thread UI only).
  const composerInteractionBlocked = isComposerInteractionBlocked({
    selectedThreadActive,
    rustChat,
  });
  // Auto-focus the composer when a thread becomes selected and the composer
  // isn't blocked. Without this, navigating into a thread from elsewhere in
  // the app (e.g. acting on a subconscious reflection in the Intelligence
  // tab — `IntelligenceSubconsciousTab.handleNavigateToReflectionThread`
  // dispatches `setSelectedThread` then routes to `/chat`) leaves focus on
  // the unmounted source button, falling back to `document.body`. The
  // textarea is rendered and enabled but ignores keystrokes until the user
  // clicks into it. Skip when there is no thread, when the composer is
  // disabled, when in voice mode, and when the user has focus on another
  // input/textarea/contenteditable (don't steal focus from a settings pane
  // the user just clicked into).
  useEffect(() => {
    if (!selectedThreadId) return;
    if (composerInteractionBlocked) return;
    if (inputMode !== 'text') return;
    const ta = textInputRef.current;
    if (!ta) return;
    const active = document.activeElement;
    if (
      active &&
      active !== document.body &&
      active !== ta &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.getAttribute('contenteditable') === 'true')
    ) {
      return;
    }
    // rAF — wait for the textarea to be in the layout tree (selectedThread
    // changes can arrive a tick before the panel mounts on first navigation).
    const id = window.requestAnimationFrame(() => {
      textInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedThreadId, composerInteractionBlocked, inputMode]);

  const isSending = Boolean(
    selectedThreadId &&
    (pendingSendingThreadIds.has(selectedThreadId) ||
      inferenceTurnLifecycleByThread[selectedThreadId] === 'started' ||
      inferenceTurnLifecycleByThread[selectedThreadId] === 'streaming')
  );

  // ── Chat mascot ────────────────────────────────────────────────────────────
  // `null` outside the merged chat surface (embedded sidebars, iOS, the Flows
  // copilot), where no mascot exists and the dock is simply not rendered.
  const chatMascot = useChatMascotOptional();
  // Stable callbacks: the mascot stage subscribes to these, and
  // `handleSendMessage` is re-created every render, so publishing it directly
  // would wake the stage on every keystroke. The ref is already maintained for
  // the dictation handler and always holds the latest send fn.
  const mascotSubmit = useCallback((text: string) => handleSendMessageRef.current?.(text), []);
  const mascotError = useCallback((message: string) => {
    setSendError(chatSendError('voice_transcription', message));
  }, []);
  useChatMascotSendBinding(chatMascot, {
    submit: mascotSubmit,
    onError: mascotError,
    // Same guard as the mic-cloud composer below: without `!selectedThreadId` a
    // transcript spoken before a thread exists hits handleSendMessage's early
    // return and is silently dropped — the user spoke into the void.
    disabled: composerInteractionBlocked || isSending || !selectedThreadId,
  });
  const mascotDock = chatMascot ? <ChatMascotDock /> : undefined;

  // Live agent activity that must stay visible even before the thread's
  // message history has loaded: an in-flight turn, recorded tool steps, a
  // processing transcript, or streamed prose. Without this, switching to a
  // thread mid-turn rendered a blank pane (the message list is gated on
  // `hasVisibleMessages`) until `loadThreadMessages` resolved — tool calls and
  // streaming output silently invisible despite landing in Redux.
  const hasLiveAgentActivity =
    isSending ||
    selectedThreadToolTimeline.length > 0 ||
    selectedThreadProcessing.length > 0 ||
    Boolean(selectedStreamingAssistant) ||
    // An interrupted turn's restored partial answer must surface too, even
    // before the durable message history loads (restore-fidelity fix 2).
    Boolean(selectedInterruptedAssistant);

  const filteredThreads = useMemo(() => {
    return threads.filter(t => isThreadVisibleInTab(t, selectedLabel));
  }, [threads, selectedLabel]);

  const sortedThreads = useMemo(() => {
    return [...filteredThreads].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
  }, [filteredThreads]);

  // Free-text search over the thread sidebar — filters the visible list by
  // title (mirrors the settings sidebar search).
  const visibleThreads = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return sortedThreads;
    return sortedThreads.filter(thread => (thread.title ?? '').toLowerCase().includes(q));
  }, [sortedThreads, threadSearch]);

  const isSidebar = variant === 'sidebar';
  // "New window" = the merged Home surface: a page-variant chat whose selected
  // thread has no messages yet. We show the greeting + banners hero above a
  // centered composer; the moment the first message lands, hasVisibleMessages
  // flips true and this collapses back to the normal conversation layout.
  const isNewWindow =
    !isSidebar &&
    !isLoadingMessages &&
    !messagesError &&
    !hasVisibleMessages &&
    !hasTaskBoard &&
    !hasLiveAgentActivity;

  // Track the floating composer footer's height so the message list can reserve
  // matching bottom padding. In the page variant the footer is absolutely
  // positioned over the scroll area, so a static padding (the old `pb-32`) gets
  // overrun whenever the footer grows — most visibly when the "Queued
  // follow-ups" panel appears mid-reply, hiding the tail of the response
  // (#4268). The sidebar variant lays the composer out in normal flow and never
  // overlaps, so we skip the observer there and keep its `pb-4`.
  useEffect(() => {
    if (isSidebar) return;
    const el = composerFooterRef.current;
    if (!el) return;
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      if (next <= 0) return;
      // Skip no-op updates. This observer watches the footer that *contains* the
      // composer, while `composerFooterHeight` feeds the message list's bottom
      // padding — so re-rendering on an unchanged measurement lets a sub-pixel
      // rounding oscillation cascade into React's nested-update limit
      // ("Maximum update depth exceeded", #5162 / TAURI-REACT-2G).
      setComposerFooterHeight(prev => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSidebar, selectedThreadId]);

  // Stable title resolver used by both the sidebar thread list and the header.
  const resolveThreadDisplayTitle = (threadId: string | null): string => {
    if (!threadId) return t('chat.selectThread');
    const thr = threads.find(th => th.id === threadId);
    return thr?.title ?? t('chat.selectThread');
  };

  // Resolve the parent of the currently-selected thread, if any. Used to
  // render the back-to-parent breadcrumb in the chat header so a user who
  // dropped into a worker thread (via `WorkerThreadRefCard` or the Tasks
  // bucket) can return to the conversation that spawned it
  // — issue #1624 acceptance criterion "Parent ↔ worker navigation is
  // bidirectional". Returns `null` when the active thread is a top-level
  // conversation (no parent), so the header stays unchanged in the
  // non-worker case.
  const selectedThreadParent = useMemo(() => {
    if (!selectedThreadId) return null;
    const current = threads.find(thr => thr.id === selectedThreadId);
    const parentId = current?.parentThreadId;
    if (!parentId) return null;
    const parent = threads.find(thr => thr.id === parentId);
    return parent
      ? { id: parent.id, title: parent.title || t('chat.parentThread') }
      : { id: parentId, title: t('chat.parentThread') };
  }, [threads, selectedThreadId, t]);

  // Thread list (left pane). Rendered through `TwoPanelLayout` below in page
  // mode; the embedded `variant="sidebar"` mode shows no thread list at all.
  const threadSidebar = (
    <ThreadList
      threads={visibleThreads}
      selectedThreadId={selectedThreadId ?? null}
      search={threadSearch}
      onSearchChange={setThreadSearch}
      onCreateThread={() => void handleCreateNewThread()}
      onSelectThread={id => {
        dispatch(setSelectedThread(id));
        void dispatch(loadThreadMessages(id));
        if (shouldSyncChatRoute) {
          navigate(chatThreadPath(id));
        }
      }}
      resolveTitle={resolveThreadDisplayTitle}
      onRequestDelete={thread =>
        setDeleteModal({
          isOpen: true,
          title: t('chat.deleteThread'),
          message: t('chat.deleteThreadConfirm').replace(
            '{title}',
            thread.title || t('chat.untitledThread')
          ),
          confirmText: t('common.delete'),
          cancelText: t('common.cancel'),
          destructive: true,
          onConfirm: () => {
            if (shouldSyncChatRoute && routeThreadId === thread.id) {
              navigate('/chat', { replace: true });
            }
            void dispatch(deleteThread(thread.id));
          },
          onCancel: () => {},
        })
      }
      editingThreadId={editingThreadId}
      editTitleValue={editTitleValue}
      editTitleInputRef={editTitleInputRef}
      onEditTitleValueChange={setEditTitleValue}
      onStartEditTitle={handleStartEditTitle}
      onCommitTitle={handleCommitTitle}
      onCancelEditTitle={() => {
        ignoreNextTitleBlurRef.current = true;
        setEditingThreadId(null);
      }}
      onBlurTitle={id => {
        if (ignoreNextTitleBlurRef.current) {
          ignoreNextTitleBlurRef.current = false;
          return;
        }
        handleCommitTitle(id);
      }}
    />
  );

  // Main chat area (right pane): header, message list, composer.
  const mainPanel = (
    <div
      className={
        isSidebar
          ? // Embedded variant keeps its own flush styling (no TwoPanelLayout).
            'flex-1 flex flex-col min-w-0 bg-surface border-l border-line overflow-hidden'
          : // Page variant: flush over the shell background. `relative` anchors
            // the absolutely-positioned floating composer.
            'relative flex-1 flex flex-col min-w-0'
      }>
      <ChatThreadView
        ref={threadViewRef}
        threadId={selectedThreadId ?? null}
        variant={variant}
        bottomPadding={!isSidebar ? composerFooterHeight + 16 : undefined}
        hasFooterContent={hasTaskBoard}
        isLoading={isLoadingMessages}
        loadError={messagesError}
        emptyContent={
          isNewWindow ? (
            <ChatNewWindowHero />
          ) : (
            <div className="flex-1 flex items-center justify-center h-full">
              <p className="text-sm text-content-secondary">{t('chat.noMessages')}</p>
            </div>
          )
        }
        shareAgentName={shareAgentName}
        scrollResetKey={location.pathname}
        pendingSendActive={selectedThreadId ? pendingSendingThreadIds.has(selectedThreadId) : false}
      />

      {/* Full-width fade so messages dissolve into the background (black/white
          per theme) behind the floating composer. Page variant only. */}
      {!isSidebar && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-28 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-black dark:via-black/90"
        />
      )}

      <div
        ref={composerFooterRef}
        data-walkthrough="home-cta"
        // Page variant: float at the bottom (absolute) over the fade; centered +
        // width-capped to match the messages. `z-20` keeps it above messages
        // that would otherwise paint over it while scrolling.
        //
        // Sidebar embed keeps the in-flow composer pinned at the bottom, but it
        // must stay reachable when the panel is too short to hold the whole
        // footer — it stacks the upsell/error banners + actionable error CTAs
        // (e.g. the voice "Setup" link) + the composer (#3785). Rather than a
        // percentage `max-height` (which does not reliably resolve inside a
        // stretched flex item in Chromium), let the footer SHRINK: dropping
        // `flex-shrink-0` and adding `min-h-0 overflow-y-auto` makes the flex
        // algorithm cap it to the available height (the basis-0 message list
        // gives up its space first) and scroll internally instead of being
        // clipped by the `overflow-hidden` mainPanel. On a tall window there is
        // free space, so the footer keeps its natural height (composer pinned).
        className={
          isSidebar
            ? 'mx-auto w-full max-w-[48.75rem] min-h-0 overflow-y-auto px-4 py-3'
            : 'absolute inset-x-0 bottom-0 z-20 mx-auto w-full max-w-[48.75rem] px-4 pb-4 pt-6'
        }>
        <>
          {isNearLimit &&
            !isAtLimit &&
            isFreeTier &&
            shouldShowBanner('conversations-warning', 24 * 60 * 60 * 1000) && (
              <div className="mb-3">
                <UpsellBanner
                  variant="warning"
                  title={t('chat.approachingLimit')}
                  message={t('chat.approachingLimitMsg').replace(
                    '{pct}',
                    String(Math.round(usagePct * 100))
                  )}
                  ctaLabel={t('chat.upgrade')}
                  onCtaClick={() => {
                    void openUrl(PRICING_URL);
                  }}
                  dismissible
                  onDismiss={() => dismissBanner('conversations-warning')}
                />
              </div>
            )}
          {teamUsage && shouldShowBudgetCompletedMessage && (
            <div className="mb-3 p-3 rounded-xl bg-coral-50 border border-coral-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <svg
                  className="w-4 h-4 text-coral-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-xs text-coral-600">
                  {teamUsage.cycleBudgetUsd > 0
                    ? `${t('chat.weeklyLimitHit')}${teamUsage.cycleEndsAt ? ` ${t('chat.resets')} ${formatResetTime(teamUsage.cycleEndsAt)}.` : ''} ${t('chat.topUpToContinue')}`
                    : t('chat.budgetComplete')}
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  data-analytics-id="chat-budget-openrouter-free"
                  disabled={openRouterStatus === 'saving'}
                  onClick={() => {
                    void handleUseOpenRouterFree();
                  }}
                  className="px-3 py-1.5 rounded-lg border border-coral-300 bg-surface text-coral-700 hover:bg-coral-100 disabled:cursor-wait disabled:opacity-70 text-xs font-medium transition-colors">
                  {openRouterStatus === 'saving'
                    ? t('openrouterFree.saving')
                    : t('openrouterFree.cta')}
                </button>
                <button
                  type="button"
                  data-analytics-id="chat-budget-top-up"
                  onClick={() => {
                    void openUrl(PRICING_URL);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-coral-500 hover:bg-coral-400 text-content-inverted text-xs font-medium transition-colors">
                  {t('chat.topUp')}
                </button>
              </div>
            </div>
          )}
          {openRouterStatus === 'error' && (
            <div className="mb-3 rounded-lg border border-coral-200 bg-coral-50 px-3 py-2 text-xs text-coral-700">
              {t('openrouterFree.error')}
            </div>
          )}

          {/* Cycle usage pill moved into ChatComposer toolbar */}
        </>

        {sendAdvisory && (
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-amber-700" data-chat-send-advisory>
              {sendAdvisory}
            </p>
            <button
              type="button"
              data-analytics-id="chat-send-advisory-dismiss"
              onClick={() => setSendAdvisory(null)}
              className="text-xs text-content-muted hover:text-content-secondary dark:text-neutral-200 dark:hover:text-neutral-200 transition-colors ml-2">
              {t('common.dismiss')}
            </button>
          </div>
        )}

        {attachError && (
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-coral-500" data-chat-send-error-code={attachError.code}>
              {attachError.message}
            </p>
            <button
              type="button"
              data-analytics-id="chat-attach-error-dismiss"
              onClick={() => setAttachError(null)}
              className="text-xs text-content-muted hover:text-content-secondary transition-colors ml-2">
              {t('common.dismiss')}
            </button>
          </div>
        )}

        {displayedSendError && (
          <div className="flex items-center justify-between mb-2">
            <p
              className="text-xs text-coral-500"
              data-chat-send-error-code={displayedSendError.code}>
              {displayedSendError.message}
            </p>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {(displayedSendError.code === 'stt_not_ready' ||
                displayedSendError.code === 'voice_transcription' ||
                displayedSendError.code === 'tts_not_ready' ||
                displayedSendError.code === 'voice_synthesis') && (
                <button
                  type="button"
                  data-analytics-id="chat-send-error-setup"
                  onClick={() => {
                    setSendError(null);
                    // STT/TTS provider settings live on the Voice panel
                    // since PR 2; the legacy local-model route was for
                    // back when speech assets were lumped with Ollama.
                    navigate('/settings/voice', settingsNavState(location));
                  }}
                  className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors">
                  {t('chat.setup')}
                </button>
              )}
              <button
                type="button"
                data-analytics-id="chat-send-error-dismiss"
                onClick={() => {
                  setSendError(null);
                  dispatch(clearCreateThreadError());
                }}
                className="text-xs text-content-muted hover:text-content-secondary dark:text-neutral-200 dark:hover:text-neutral-200 transition-colors">
                {t('common.dismiss')}
              </button>
            </div>
          </div>
        )}

        {(() => {
          // Surface a parked ApprovalGate request for the shown thread just
          // above the composer, so it stays visible regardless of scroll.
          const approvalThreadId = selectedThreadId ?? firstActiveThreadId;
          const pendingApproval = approvalThreadId
            ? pendingApprovalByThread[approvalThreadId]
            : undefined;
          if (!pendingApproval || !approvalThreadId) return null;
          // `composio_connect` parks on the same gate but needs a Connect
          // button + OAuth poll rather than approve/deny (#3993).
          const isConnect = pendingApproval.toolName === 'composio_connect';
          return (
            <div className="mb-2">
              {isConnect ? (
                // Key by requestId so switching from one parked approval to
                // another remounts the card with fresh local state (phase,
                // field values, cancellation refs, poll timers) instead of
                // bleeding the previous request's state in (#4062, coderabbit).
                <IntegrationConnectCard
                  key={pendingApproval.requestId}
                  threadId={approvalThreadId}
                  approval={pendingApproval}
                />
              ) : (
                <ApprovalRequestCard
                  key={pendingApproval.requestId}
                  threadId={approvalThreadId}
                  approval={pendingApproval}
                />
              )}
            </div>
          );
        })()}

        {/* Flow-approval surface (chat): actionable banner(s) for paused
            tinyflows runs, pushed via the `flow_approval_request` socket
            event (issue: flow-approval surfacing). Not gated on the selected
            thread — see the hook call above for why — so every pending
            request renders regardless of which thread is open. */}
        {flowApprovalRequests.length > 0 && (
          <div className="mb-2 flex flex-col gap-2">
            {flowApprovalRequests.map(request => (
              <FlowApprovalRequestCard
                key={request.request_id}
                request={request}
                onResolved={dismissFlowApprovalRequest}
              />
            ))}
          </div>
        )}

        {(() => {
          // Surface in-flight + failed artifact cards above the composer
          // (#2779). Mirrors the approval-card placement so the user sees
          // the spinner / error without scrolling. `ready` cards are
          // delegated to the header ChatFilesChip panel (#3024) so the
          // chat scroll area isn't permanently occupied — restored decks
          // are listable from the chip on demand.
          //
          // The failed-card Retry button re-dispatches the producing tool
          // via `ai_regenerate` (#3162): the core reloads the persisted
          // creation args and re-runs generation under the original
          // artifact id, so the card swaps back to a spinner in place and
          // then to ready/failed via the socket events.
          const artifactThreadId = selectedThreadId ?? firstActiveThreadId;
          const all = artifactThreadId ? (artifactsByThread[artifactThreadId] ?? []) : [];
          const live = all.filter(a => a.status !== 'ready');
          if (live.length === 0) return null;
          return (
            <div className="mb-2 flex flex-col gap-2">
              {live.map(artifact => (
                <ArtifactCard
                  key={artifact.artifactId}
                  artifact={artifact}
                  onRetry={
                    artifactThreadId
                      ? id => {
                          void aiRegenerate(id, artifactThreadId).catch(err => {
                            console.warn('[artifact] regenerate failed:', err);
                          });
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          );
        })()}

        {/* Thread-scoped todo list the agent maintains as it works — read-only,
            pinned above the composer. Distinct from the Intelligence-tab kanban
            (global `user-tasks`). Renders nothing when the thread has no active
            cards. */}
        {/* Plan-mode review: the orchestrator parked the live turn on a
            thread-scoped plan (request_plan_review gate). Surface it for the
            user to Approve / Reject / send feedback on before anything executes;
            the card resolves the parked turn via plan_review_decide. */}
        {selectedThreadId && pendingPlanReview && (
          // Key by request id so a re-parked (revised) plan — or a thread switch —
          // remounts the card and resets its local decision/feedback state,
          // matching the ApprovalRequestCard pattern above.
          <PlanReviewCard
            key={pendingPlanReview.requestId}
            threadId={selectedThreadId}
            review={pendingPlanReview}
          />
        )}

        {/* Agent-first Workflow authoring (issue B4): the agent drafted a
            candidate automation via `propose_workflow`. The tool only
            validates — it never creates the flow — so this card is the ONLY
            path from proposal to saved automation via "Save & enable"
            (`flows_create`), or the user can Dismiss it outright. */}
        {selectedThreadId && pendingWorkflowProposal && (
          // Keyed by name so a second proposal in the same thread (before the
          // first is resolved) remounts the card and resets its local
          // saving/error state, matching the PlanReviewCard pattern above.
          <WorkflowProposalCard
            key={pendingWorkflowProposal.name}
            threadId={selectedThreadId}
            proposal={pendingWorkflowProposal}
          />
        )}

        {selectedThreadId && (
          <ThreadTodoStrip
            board={selectedTaskBoard}
            disabled={!selectedThreadId}
            onViewSession={card => {
              if (!card.sessionThreadId) return;
              // Navigation only — do NOT mark the thread active. activeThreadId
              // tracks a true in-flight turn; forcing a completed session active
              // would wedge the composer.
              dispatch(setSelectedThread(card.sessionThreadId));
              void dispatch(loadThreadMessages(card.sessionThreadId));
              if (shouldSyncChatRoute) {
                navigate(chatThreadPath(card.sessionThreadId));
              }
            }}
          />
        )}

        {/* Cancel the in-flight turn for composer modes that don't render the
            text ChatComposer (mic-cloud + voice). The text composer carries its
            own in-box Stop button, so the footer control only appears for the
            non-text branches — otherwise voice/mic flows would have no way to
            stop a long-running generation. */}
        {isSending && rustChat && (composer === 'mic-cloud' || inputMode !== 'text') && (
          <div className="mb-2 flex justify-start px-1">
            <button
              type="button"
              data-analytics-id="chat-cancel-generation"
              onClick={handleStopGeneration}
              className="text-xs text-content-muted transition-colors hover:text-content-secondary">
              {t('common.cancel')}
            </button>
          </div>
        )}

        {composer === 'mic-cloud' ? (
          // `relative` so the mascot dock (absolute, `bottom-full`) anchors here
          // — this branch renders no ChatComposer to hang it off.
          <div className="relative flex flex-col items-center gap-3 py-1">
            {mascotDock}
            <MicComposer
              // Without `!selectedThreadId`, a mic submit before a thread is
              // ready hits `handleSendMessage`'s early return and the
              // transcript is silently dropped — the user spoke into the void.
              disabled={composerInteractionBlocked || isSending || !selectedThreadId}
              onSubmit={text => handleSendMessage(text)}
              onError={message => setSendError(chatSendError('voice_transcription', message))}
              showDeviceSelector
              onSwitchToText={() => setComposerOverride('text')}
            />
          </div>
        ) : inputMode === 'text' ? (
          <>
            <ChatComposer
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSend={handleComposerSend}
              onStopGeneration={rustChat ? handleStopGeneration : undefined}
              textInputRef={textInputRef}
              fileInputRef={fileInputRef}
              composerInteractionBlocked={composerInteractionBlocked}
              isSending={isSending}
              allowParallelSend={selectedThreadActive}
              attachments={attachments}
              onAttachFiles={handleAttachFiles}
              onRemoveAttachment={id => setAttachments(prev => prev.filter(a => a.id !== id))}
              attachError={attachError}
              onSwitchToMicCloud={() => setComposerOverride('mic-cloud')}
              handleInputKeyDown={handleInputKeyDown}
              inlineCompletionSuffix=""
              isComposingTextRef={isComposingTextRef}
              maxAttachments={ATTACHMENT_MAX_IMAGES + ATTACHMENT_MAX_FILES}
              // Empty → no native `accept` filter (it greys valid files on
              // macOS/CEF). Type enforcement happens in handleAttachFiles via
              // validateAndReadFile, which honors modelSupportsVision.
              allowedMimeTypes={[]}
              attachmentsEnabled={CHAT_ATTACHMENTS_ENABLED}
              // Header stack above the input box (outside its blue focus ring):
              // queued follow-ups + the thread-goal editor (opened via the
              // footer "Set goal" trigger). Entries that render null are no-ops.
              headerSlots={[
                selectedThreadId && (queuedFollowupsByThread[selectedThreadId]?.length ?? 0) > 0 ? (
                  <QueuedFollowups
                    key="queued-followups"
                    items={queuedFollowupsByThread[selectedThreadId] ?? []}
                    onClear={() => void handleClearQueuedFollowups()}
                  />
                ) : null,
                <ThreadGoalEditorPanel key="thread-goal" ctl={threadGoal} />,
              ]}
              mascotDock={mascotDock}
            />
          </>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-analytics-id="chat-voice-switch-to-text"
              onClick={() => setInputMode('text')}
              disabled={isRecording || isTranscribing}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-line bg-surface text-content-muted hover:text-content-secondary dark:text-neutral-200 dark:hover:text-neutral-200 hover:border-line-strong dark:hover:border-line-strong transition-colors disabled:opacity-40"
              title={t('chat.switchToText')}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M4 6h16M4 12h10m-10 6h16"
                />
              </svg>
            </button>
            <button
              type="button"
              data-analytics-id="chat-voice-record-toggle"
              onClick={() => {
                void handleVoiceRecordToggle();
              }}
              disabled={!rustChat || isSending || isTranscribing || !canUseMicrophoneApi}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isRecording
                  ? 'bg-coral-500 hover:bg-coral-400 text-content-inverted'
                  : 'bg-primary-600 hover:bg-primary-500 text-content-inverted'
              } disabled:opacity-40 disabled:cursor-not-allowed`}>
              {isTranscribing
                ? t('chat.transcribing')
                : isRecording
                  ? t('chat.stopAndSend')
                  : t('chat.startTalking')}
            </button>
            <p className="text-xs text-content-faint truncate">
              {voiceStatus ??
                (isPlayingReply && replyMode === 'voice'
                  ? t('chat.playingVoiceReply')
                  : canUseMicrophoneApi
                    ? t('chat.voiceHint')
                    : t('chat.micUnavailable'))}
            </p>
          </div>
        )}
        {/* Worker-thread back-to-parent breadcrumb (page variant) — its own line. */}
        {!isSidebar && selectedThreadParent && (
          <button
            type="button"
            data-analytics-id="chat-header-back-to-parent-thread"
            onClick={() => {
              dispatch(setSelectedThread(selectedThreadParent.id));
              void dispatch(loadThreadMessages(selectedThreadParent.id));
              navigate(chatThreadPath(selectedThreadParent.id));
            }}
            className="mt-2 flex items-center gap-1 rounded px-1 text-[11px] font-medium text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            data-testid="worker-thread-back-to-parent">
            <span aria-hidden="true">←</span>
            <span className="max-w-[16rem] truncate">
              {t('chat.backToThread').replace('{title}', selectedThreadParent.title)}
            </span>
          </button>
        )}

        {/* Thread title + inline rename moved to the sidebar thread list rows. */}

        {/* Model + token stats (left) and the quick/reasoning toggle + files
            chip (right) share one line. */}
        <div
          className="mt-2 flex items-center justify-between gap-2"
          data-walkthrough="chat-agent-panel">
          <div className="flex min-w-0 items-center gap-2">
            <ComposerTokenStats model={resolvedModel} threadId={selectedThreadId} />
            {/* Set/show the thread goal; click opens the editor above the composer. */}
            <ThreadGoalFooterTrigger ctl={threadGoal} />
          </div>
          {!isSidebar && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <div
                className="flex h-7 items-center rounded-full border border-line bg-surface-subtle p-0.5"
                role="radiogroup"
                aria-label={t('chat.agentProfile.label')}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedAgentProfileId === 'default'}
                  data-analytics-id="chat-header-mode-quick"
                  onClick={() => void handleSelectAgentProfile('default')}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all ${
                    selectedAgentProfileId === 'default'
                      ? 'bg-surface text-content shadow-sm'
                      : 'text-content-muted hover:text-content-secondary'
                  }`}>
                  {t('chat.agentProfile.quick')}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedAgentProfileId === 'reasoning'}
                  data-analytics-id="chat-header-mode-reasoning"
                  onClick={() => void handleSelectAgentProfile('reasoning')}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all ${
                    selectedAgentProfileId === 'reasoning'
                      ? 'bg-surface text-content shadow-sm'
                      : 'text-content-muted hover:text-content-secondary'
                  }`}>
                  {t('chat.agentProfile.reasoning')}
                </button>
              </div>
              {/* Super context is read at thread construction, so it only
                  affects NEW threads. Hide the toggle once the thread has ANY
                  activity — use the raw `messages` (not `hasVisibleMessages`,
                  which ignores hidden transcript entries) so an already-started
                  thread never looks "fresh" here. */}
              {/* Key by thread so switching to another empty chat remounts the
                  toggle and re-runs its off-by-default reset (PR #4874 review). */}
              {messages.length === 0 && <SuperContextToggle key={selectedThreadId ?? 'new-chat'} />}
              {selectedThreadId && (
                <button
                  type="button"
                  data-testid="background-processes-toggle"
                  data-analytics-id="chat-header-background-processes"
                  onClick={() => threadViewRef.current?.openBackgroundProcesses()}
                  aria-label={t('conversations.backgroundTasks.title')}
                  title={
                    backgroundProcesses.length > 0
                      ? t('conversations.backgroundTasks.titleWithCount').replace(
                          '{count}',
                          String(backgroundProcesses.length)
                        )
                      : t('conversations.backgroundTasks.title')
                  }
                  className="relative flex h-7 w-7 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-hover hover:text-content-secondary">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                    />
                  </svg>
                  {runningBackgroundCount > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-amber-500 px-0.5 text-[9px] font-semibold leading-none text-content-inverted">
                      {runningBackgroundCount}
                    </span>
                  ) : memorySyncActive ? (
                    <span
                      data-testid="background-activity-dot"
                      className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full bg-amber-500"
                    />
                  ) : null}
                </button>
              )}
              {(selectedThreadId ?? firstActiveThreadId) && (
                <ChatFilesChip threadId={(selectedThreadId ?? firstActiveThreadId) as string} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={
        isSidebar
          ? 'h-full relative z-10 flex overflow-hidden'
          : 'h-full relative z-10 flex justify-center overflow-hidden bg-surface/70 dark:bg-black/40'
      }>
      {isSidebar ? (
        <>
          {projectThreadList && (
            <SidebarContent>
              <div className="order-1 flex h-full min-h-0 flex-col overflow-hidden">
                {threadSidebar}
              </div>
            </SidebarContent>
          )}
          {mainPanel}
        </>
      ) : (
        // The thread list always lives in the root app sidebar's dynamic region
        // (order-1 so any app rail projected by the parent sits above it). The
        // chat pane keeps a comfortable, centered reading width.
        <>
          <SidebarContent>
            <div className="order-1 flex h-full min-h-0 flex-col overflow-hidden">
              {threadSidebar}
            </div>
          </SidebarContent>
          <div className="flex h-full w-full">{mainPanel}</div>
        </>
      )}
      <ConfirmationModal
        modal={deleteModal}
        onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default Conversations;

/**
 * Embeddable variant — same component, page layout (floating centered
 * card). Mounted inside /accounts when the Agent entry is selected.
 */
export const ConversationsPage = () => <Conversations variant="page" />;
